import { Box, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import CustomDialog from "app/components/CustomDialog";
import ListWrapper from "app/components/ListWrapper";
import ResponseSnackbar from "app/components/ResponseSnackbar";
import useGetCandidateApplication from "app/hooks/candidate/useGetCandidateApplication";
import useDeleteJob from "app/hooks/job/useDeleteJob";
import useGetJob from "app/hooks/job/useGetJob";
import useGetJobs from "app/hooks/job/useGetJobs";
import { useUpdateJob } from "app/hooks/job/useUpdateJob";
import { useGetSkills } from "app/hooks/skill/useGetSkills";
import useAuth from "app/hooks/useAuth";
import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useCheckJobsData from "./hooks/useCheckJobsData";
import { useExtractJobData } from "./hooks/useExtractTableData";
import { useGetJobTableHeaders } from "./hooks/useGetJobTableHeaders";
import { useGetTableActions as useGetRowActions } from "./hooks/useGetTableActions";
import ApplyDialogContent2 from "./shared/ApplyDialogContent2";
import JobListFilter from "./shared/JobListFilter";
import PostJobForm from "./shared/PostJobForm";
import { filterReducer, filterState } from "./shared/Reducer/FilterReducer";
import { actionTypes, initialState, paginationReducer } from "./shared/Reducer/PaginationReducer";
import PaginationTableAlt from "../material-kit/tables/PaginationTableAlt";
import UpdateApplicationDialog from "../applied_candidate/shared/application_detail_components/UpdateApplicationDialog";
import UpdateJobStatusDialog from "./shared/UpdateJobStatusDialog";
import useEnableDisableJob from "app/hooks/job/useEnableDisableJob";
import { StatusOptions } from "app/utils/global_constants";

function JobListings(props) {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isCompanyUser = location.pathname.includes("company");
  const [dialogState, setDialogState] = useState({
    editOpen: false,
    deleteOpen: false,
    detailOpen: false,
    applyOpen: false,
    statusOpen: false
  });

  const [pagination, dispatchPagination] = useReducer(paginationReducer, initialState);
  const [selectedRowId, setSelectedRowId] = useState();
  const [responseState, setResponseState] = useState({
    error: false,
    success: false,
    message: ""
  });
  // For job filtering
  const [filter, dispatchFilter] = useReducer(filterReducer, filterState);
  const queryClient = useQueryClient();

  // Debounce logic for search
  const debounceTimeout = useRef();
  const DEBOUNCE_DELAY = 350;

  // Unified handler for search (debounced)
  const handleSearch = useCallback(
    (searchValue) => {
      const trimmedValue = searchValue?.trim() || "";
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
      debounceTimeout.current = setTimeout(() => {
        dispatchPagination({ type: actionTypes.SET_SEARCH_TERM, payload: trimmedValue });
        dispatchPagination({ type: actionTypes.SET_PAGE, payload: 0 });
        queryClient.invalidateQueries({ queryKey: ["job_listings"] });
      }, DEBOUNCE_DELAY);
    },
    [queryClient]
  );

  // Unified handler for filter change
  const handleFilterChange = useCallback(
    (type, value) => {
      dispatchFilter({ type, payload: value });
      dispatchPagination({ type: actionTypes.SET_PAGE, payload: 0 });
      queryClient.invalidateQueries({ queryKey: ["job_listings"] });
    },
    [queryClient]
  );

  // Update job details
  const {
    data: updatedJob,
    mutate: mutateJob,
    isPending: isUpdating
  } = useUpdateJob({
    id: selectedRowId,
    onSuccess: (data) => {
      setSelectedRowId(undefined);
      queryClient.invalidateQueries({ queryKey: ["get_single_job"] });
      queryClient.invalidateQueries({ queryKey: ["job_listings"] });
      setResponseState({
        error: false,
        success: true,
        message: "Job post updated successfully"
      });
    },
    onError: () => {
      setResponseState({ error: true, success: false, message: "Error updating job post" });
    },
    onSettled: () => setDialogState((prevState) => ({ ...prevState, editOpen: false }))
  });

  // Normalize type and location to arrays of strings
  const normalizedType = Array.isArray(filter.type)
    ? filter.type.map((t) => (typeof t === "object" && t.label ? t.label : t))
    : [];
  const normalizedLocation = Array.isArray(filter.location)
    ? filter.location.map((l) => (typeof l === "object" && l.label ? l.label : l))
    : [];

  // Get all jobs posted (always use trimmed search and all filters)
  const {
    data: allJobs,
    isFetching: isAllJobsFetching,
    isFetched: isJobsFetched
  } = useGetJobs({
    sortOrder: pagination.sortOrder,
    column: "",
    limit: pagination.limit,
    offset: pagination.offset,
    searchTerm: pagination.searchTerm?.trim() || "",
    isUpdating,
    type: normalizedType,
    exp: filter.exp.value, // <-- always normalized
    city: filter.city,
    salary: filter.salary ? [filter.salary[0] * 100000, filter.salary[1] * 100000] : null,
    visibility: filter.visibility,
    isExpired: filter.expirationStatus,
    location: normalizedLocation
  });

  // Get jobs applied by the candidate (enabled only for candidate login)
  const { data: appliedJobs } = useGetCandidateApplication({
    id: user.id,
    debounceSearch: "",
    status: StatusOptions.map((status) => ({ value: status.value })), // send all statuses
    enabled: user.role === "candidate"
  });

  // Get a single job detail
  const { data: selectedJob, isSuccess: isSelectedJob } = useGetJob(
    selectedRowId,
    updatedJob?.job?.jp_updated_at
  );

  // Delete a job
  const { mutate: deleteJob, isPending: isDeleting } = useDeleteJob({
    id: selectedRowId,
    updateDelete: { jp_is_deleted: true },
    onSuccess: () => {
      setDialogState((prevState) => ({ ...prevState, deleteOpen: false }));
      setSelectedRowId(undefined);
      queryClient.invalidateQueries({ queryKey: ["job_listings"] });
      setResponseState((prevValue) => ({
        ...prevValue,
        success: true,
        message: "Job post deleted successfully"
      }));
    },
    onError: () => {
      setResponseState((prevValue) => ({ ...prevValue, error: true, message: "Error deleting" }));
    },
    onSettled: () => {
      setDialogState((prevState) => ({ ...prevState, deleteOpen: false }));
    }
  });

  // Enable-Disable a job
  const { mutate: updateJobVisibility } = useEnableDisableJob({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job_listings"] });
      setSelectedRowId(undefined);
      setDialogState((prevState) => ({ ...prevState, statusOpen: false }));
      setResponseState({
        error: false,
        success: true,
        message: "Job status updated successfully"
      });
    },
    onError: () => {}
  });

  // Get skill list
  const { data: skills } = useGetSkills({ keys: [] });

  // Row Actions are the actions at the end of each row like 'Edit', 'Delete', 'View' etc
  const RowActions = useGetRowActions((row, type) => {
    setSelectedRowId(row.jp_id);
    setDialogState((prevState) => ({ ...prevState, [type]: true }));
  });

  const { tableData, count } = useCheckJobsData(isJobsFetched, allJobs);
  const extractedData = useExtractJobData(tableData, appliedJobs || []);
  const ColumnHeaders = useGetJobTableHeaders();

  // Sync URL with both search and filter state
  useEffect(() => {
    const newUrl = new URL(window.location);
    if (pagination.searchTerm) newUrl.searchParams.set("search", pagination.searchTerm);
    else newUrl.searchParams.delete("search");
    if (filter.type && filter.type.length) newUrl.searchParams.set("type", filter.type.join(","));
    else newUrl.searchParams.delete("type");
    if (filter.exp) newUrl.searchParams.set("exp", filter.exp);
    else newUrl.searchParams.delete("exp");
    if (filter.city) newUrl.searchParams.set("city", filter.city);
    else newUrl.searchParams.delete("city");
    if (filter.salary) newUrl.searchParams.set("salary", filter.salary.join(","));
    else newUrl.searchParams.delete("salary");
    if (filter.location && filter.location.length)
      newUrl.searchParams.set("location", filter.location.join(","));
    else newUrl.searchParams.delete("location");
    navigate(newUrl.pathname + newUrl.search, { replace: true });
  }, [pagination.searchTerm, filter, navigate]);

  // Clean up debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
  }, []);

  const handleRowClick = (row) => {
    const formatForUrl = (text) =>
      encodeURIComponent(
        text
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9\s]/gi, "") // remove special chars
          .replace(/\s+/g, "-") // replace spaces with hyphens
      );

    const jobTitleSlug = formatForUrl(row.jp_title);
    const companyNameSlug = formatForUrl(row.jp_company_name);

    const url = `/jobs/job-detail/${jobTitleSlug}-${companyNameSlug}/${row.jp_id}`;
    navigate(url);
    // window.open(url, "_blank");
  };

  const handleUpdateJobVisibility = (jobId, status, updatedBy) => {
    updateJobVisibility({ jobId, status, updatedBy });
  };

  return (
    <ListWrapper
      editForm={
        isSelectedJob && (
          <PostJobForm
            mutationFn={mutateJob}
            skills={skills}
            preloadedData={selectedJob}
            title={"Edit Job Details"}
          />
        )
      }
      editTitle={"Edit Job"}
      editOpen={dialogState.editOpen}
      handleEditClose={() => setDialogState((prevState) => ({ ...prevState, editOpen: false }))}
      onChangeSearch={handleSearch}
      searchlabel={isCompanyUser ? "Search By Job Title" : "Search By Job Title / Company Name"}
      alertTitle={"Are you sure you want to delete this job post"}
      alertMessage={"This action is not permanent. You can undo this action in the 'Trash' page."}
      alertOpen={dialogState.deleteOpen}
      deleteLoadingState={isDeleting}
      alertCloseAction={() => setDialogState((prevState) => ({ ...prevState, deleteOpen: false }))}
      alertAcceptAction={deleteJob}
    >
      {/* Success Message */}
      <ResponseSnackbar
        open={responseState.success}
        message={responseState.message}
        name={"success"}
        severity={"success"}
        onClose={() => setResponseState({ error: false, success: false, message: "" })}
      />
      {/* Error Message */}
      <ResponseSnackbar
        open={responseState.error}
        message={responseState.message}
        name={"error"}
        onClose={() => setResponseState({ error: false, success: false, message: "" })}
        severity={"error"}
      />
      {/* Apply Dialog */}
      {selectedJob && (
        <CustomDialog
          dialogTitle={`Apply for ${selectedJob.jp_title}`}
          fullWidth
          maxWidth={"sm"}
          onClose={() =>
            setDialogState({
              editOpen: false,
              deleteOpen: false,
              detailOpen: false,
              applyOpen: false
            })
          }
          open={dialogState.applyOpen}
        >
          <ApplyDialogContent2
            job={selectedJob}
            onClose={() =>
              setDialogState({
                editOpen: false,
                deleteOpen: false,
                detailOpen: false,
                applyOpen: false
              })
            }
          />
        </CustomDialog>
      )}
      {/*  */}
      {selectedJob && (
        <UpdateJobStatusDialog
          open={dialogState.statusOpen}
          onClose={() => setDialogState((prevState) => ({ ...prevState, statusOpen: false }))}
          onConfirm={handleUpdateJobVisibility}
          selectedJob={selectedJob}
        />
      )}

      {/* Job Listing Filter */}
      <JobListFilter
        filter={filter}
        dispatchFilter={(action) => handleFilterChange(action.type, action.payload)}
      />
      {/* Job Fetching Progress */}
      {isAllJobsFetching && (
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"} width={"100%"} my={2}>
          <CircularProgress />
        </Box>
      )}
      <div style={{ height: "20px" }}></div>
      {/* Job Listing Table */}
      {!isAllJobsFetching && extractedData && (
        <PaginationTableAlt
          data={extractedData}
          count={count}
          columnHeaders={ColumnHeaders}
          columnSort={["Title"]}
          rowsPerPage={pagination.limit}
          rowHover
          tooltipLabel={"Click To View Details"}
          currentPage={pagination.page}
          sortOrder={pagination.sortOrder}
          handleChangeRowsPerPage={(event) =>
            dispatchPagination({ type: actionTypes.SET_LIMIT, payload: +event.target.value })
          }
          handleRowClick={(row) => handleRowClick(row)}
          handleChangePage={(_, newPage) => {
            dispatchPagination({ type: actionTypes.SET_PAGE, payload: newPage });
            dispatchPagination({ type: actionTypes.SET_OFFSET, payload: newPage });
          }}
          handleSortOrder={(columnHeader) => {
            if (columnHeader === "Title") {
              dispatchPagination({ type: actionTypes.SET_COLUMN, payload: "jp_title" });
            }
            dispatchPagination({ type: actionTypes.SET_SORT_ORDER });
          }}
          actions={RowActions}
        />
      )}
    </ListWrapper>
  );
}

export default JobListings;
